<template>
  <v-form
    ref="form"
    v-model="valid"
    lazy-validation
    @submit.prevent="submit"
    @keypress.enter="submit"
  >
    <p class="text-justify mb-5">
      Insira e confirme sua nova senha. De preferência utilize uma senha forte.
    </p>

    <t-password-input v-model="password" :rules="rules.password" label="Senha" />

    <t-password-input
      v-model="password_confirm"
      :rules="rules.password_confirm"
      label="Confirmar Senha"
    />

    <t-btn
      block
      color="success"
      type="submit"
      class="heading--text"
      large
      :loading="loading"
    >
      Alterar Senha
    </t-btn>
  </v-form>
</template>

<script setup lang="ts">
import { ref, computed, useTemplateRef, type Ref } from "vue";
import type User from "@/types/User";
import TBtn from "@/components/core/TBtn.vue";
import axios, { type AxiosError } from "axios";
import type ErrorResponse from "@/types/ErrorResponse";
import { type PasswordToken } from "@/api/requests/getPasswordRecoveryToken";
import TPasswordInput from "../core/TPasswordInput.vue";
import recoverPassword from "@/api/requests/recoverPassword";
import { useSnackbar } from "@/stores/snackbar";

interface Props {
  modelValue?: User;
  token: PasswordToken;
}

const {
  modelValue = {} as User,
  token,
} = defineProps<Props>();

const emit = defineEmits<{
  "submit": [success: boolean];
  "update:modelValue": [user: User];
}>();

defineOptions({
  name: "FormValidateRecoverCode",
});

const snackbar = useSnackbar();
const form = useTemplateRef<{ validate: () => boolean }>("form");

const valid: Ref<boolean> = ref(true);
const loading: Ref<boolean> = ref(false);
const password: Ref<string> = ref("");
const password_confirm: Ref<string> = ref("");

const user = computed<User>(() => modelValue);

const rules = computed(() => ({
  password: [
    (v: string) => !!v || "O campo senha é obrigatório",
    (v: string) => (v && v.length >= 8) || "A senha deve ter no mínimo 8 caracteres",
  ],
  password_confirm: [ (v: string) => (!!v && v === password.value) || "As senhas não coincidem" ],
}));

async function submit(): Promise<void> {
  try {
    if (form.value?.validate()) {
      loading.value = true;

      const success = await recoverPassword(token, password.value, user.value.document);

      emit("submit", success);
    }
  } catch (e) {
    if (axios.isAxiosError(e)) {
      const error = e as AxiosError<ErrorResponse>;

      snackbar.show(error.message, "error");
    }
  } finally {
    loading.value = false;
  }
}
</script>
